import { useState } from 'react';
import { copyToClipboard, guideLink } from '../lib/share';

export type GuideId = 'host' | 'vr' | 'browser';

interface GuideStep {
  headline: string; // ≤6 words, one action
  detail: string; // ≤1 line
  icon: string;
}

interface GuideSpec {
  id: GuideId;
  title: string;
  promise: string;
  icon: string;
  steps: GuideStep[]; // max 6
  remedy: { question: string; fix: string };
  /** where the room code block sits relative to steps */
  codeAfterStep: number;
}

/**
 * The three infographic how-to guides — one template, per the PRD spec:
 * marquee header + bulb strip, numbered gold discs (≤6 one-action steps),
 * a huge room-code block, and exactly one amber "if it's stuck" remedy each
 * (the three real-night burns: asleep controllers / firewall bat / mic
 * permission). Hard rule: no paragraph over 2 lines.
 */
const GUIDES: Record<GuideId, GuideSpec> = {
  host: {
    id: 'host',
    title: 'Host a Night',
    promise: 'From movie file to open theater in four steps.',
    icon: '🎬',
    steps: [
      { headline: 'Drop your movie in', detail: 'movie.mp4 into the game folder — it’s found automatically.', icon: '🎞' },
      { headline: 'Double-click _HOST.bat', detail: 'in dist\\Windows — the theater opens itself.', icon: '🖱' },
      { headline: 'Read your room code', detail: 'on screen in-game — ports open automatically.', icon: '👀' },
      { headline: 'Send the invites', detail: 'schedule the night; we text and email everyone.', icon: '📨' },
    ],
    remedy: {
      question: 'Friends can’t connect?',
      fix: 'Run the firewall-allow bat once, and heed the red port warning on screen.',
    },
    codeAfterStep: 3,
  },
  vr: {
    id: 'vr',
    title: 'Join in VR',
    promise: 'Headset on to seated in under a minute.',
    icon: '🥽',
    steps: [
      { headline: 'Open Thursday Night', detail: 'Quest Library — accept the ALPHA invite once.', icon: '🥽' },
      { headline: 'Wake your controllers', detail: 'press any button until they glow — the #1 silent failure.', icon: '🎮' },
      { headline: 'Enter the room code', detail: 'from your invite text.', icon: '🔢' },
      { headline: 'Walk into the theater', detail: 'your seat’s waiting — voice just works.', icon: '🎟' },
    ],
    remedy: {
      question: 'Stuck on connecting?',
      fix: 'Controllers are probably asleep — press buttons, then retry the code.',
    },
    codeAfterStep: 2,
  },
  browser: {
    id: 'browser',
    title: 'Watch in Your Browser',
    promise: 'No headset needed — any phone or computer.',
    icon: '🖥',
    steps: [
      { headline: 'Go to thursday-night.com', detail: 'phone or computer, any browser.', icon: '🌐' },
      { headline: 'Enter the room code', detail: 'the big box on the front page.', icon: '🔢' },
      { headline: 'Allow the microphone', detail: 'that’s your voice in the theater.', icon: '🎙' },
      { headline: 'You’re in the room', detail: 'watch along, as a ghost in the seats.', icon: '👻' },
    ],
    remedy: {
      question: 'No mic prompt?',
      fix: 'Check the address-bar site settings and allow the microphone, then reload.',
    },
    codeAfterStep: 1,
  },
};

export function guideTitle(id: GuideId): string {
  return GUIDES[id].title;
}

export function RoomCodeBlock({ code, hint }: { code?: string; hint?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="code-block">
      <div className="code-block-label">Room code</div>
      <div className={`code-block-code${code ? '' : ' placeholder'}`}>
        {code ?? 'YOUR ROOM CODE'}
      </div>
      {code ? (
        <button
          className="btn secondary small"
          onClick={async () => {
            await copyToClipboard(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }}
        >
          {copied ? 'Copied!' : 'Copy code'}
        </button>
      ) : (
        <div className="code-block-hint">{hint ?? 'it’s in your invite text'}</div>
      )}
    </div>
  );
}

/** Desktop-class device check — the only place a thursdaynight:// deep link
 * can reach the installed Windows game. Fine pointer AND a non-mobile UA. */
function isDesktop(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const fine = window.matchMedia ? window.matchMedia('(pointer: fine)').matches : true;
  return fine && !/Mobi|Android/i.test(navigator.userAgent);
}

/** Quiet secondary "join from this PC" affordance next to a room code — the
 * game registers the thursdaynight:// protocol handler at install. Renders
 * nothing on phones/tablets, where the link can't do anything. */
export function PcJoinButton({ code }: { code: string }) {
  if (!isDesktop()) return null;
  return (
    <div className="pc-join">
      <a className="btn secondary small" href={`thursdaynight://join/${encodeURIComponent(code)}`}>
        🖥 Open the theater on this PC
      </a>
      <div className="hint-line">needs the Windows game installed</div>
    </div>
  );
}

export function GuidePage({ guide, roomCode }: { guide: GuideId; roomCode?: string }) {
  const g = GUIDES[guide];
  const others = (Object.keys(GUIDES) as GuideId[]).filter((k) => k !== guide);
  return (
    <div className="guide">
      <div className="card marquee bulbs guide-head">
        <div className="guide-head-icon" aria-hidden="true">
          {g.icon}
        </div>
        <h1 className="display">{g.title}</h1>
        <p className="soft promise">{g.promise}</p>
      </div>

      <ol className="guide-steps">
        {g.steps.map((s, i) => (
          <li className="guide-step" key={s.headline}>
            <div className="disc-rail">
              <div className="disc display">{i + 1}</div>
              {i < g.steps.length - 1 && <div className="rail" aria-hidden="true" />}
            </div>
            <div className="guide-step-body card">
              <h3>{s.headline}</h3>
              <p className="muted">{s.detail}</p>
            </div>
            <div className="guide-step-icon" aria-hidden="true">
              {s.icon}
            </div>
          </li>
        ))}
      </ol>

      <RoomCodeBlock
        code={roomCode}
        // hosts never get an invite text — their code appears in-game
        hint={guide === 'host' ? 'it’s on screen in-game once you host' : undefined}
      />
      {guide === 'vr' && roomCode && <PcJoinButton code={roomCode} />}

      <div className="callout-amber">
        <span className="callout-icon" aria-hidden="true">
          ⚠
        </span>
        <div>
          <strong>{g.remedy.question}</strong> {g.remedy.fix}
        </div>
      </div>

      <div className="guide-foot">
        <p className="soft">Still stuck? Text the host.</p>
        <div className="guide-foot-links">
          {others.map((o) => (
            <a className="btn secondary small" key={o} href={`?g=${o}`}>
              {GUIDES[o].icon} {GUIDES[o].title}
            </a>
          ))}
        </div>
        <div className="guide-foot-art" aria-hidden="true" />
      </div>
    </div>
  );
}

/** The "How it works" row of three guide cards (home + create-flow success). */
export function GuidesRow({ roomCode }: { roomCode?: string }) {
  return (
    <div className="guides-row">
      {(Object.keys(GUIDES) as GuideId[]).map((id) => {
        const g = GUIDES[id];
        const href = roomCode ? guideLink(id, roomCode) : `?g=${id}`;
        return (
          <a className="card guide-card" key={id} href={href}>
            <span className="guide-card-icon" aria-hidden="true">
              {g.icon}
            </span>
            <span className="guide-card-title display">{g.title}</span>
            <span className="guide-card-sub">{g.promise}</span>
          </a>
        );
      })}
    </div>
  );
}
